/* Additive presentation only. Dawn remains authoritative for options, prices and cart requests. */
(() => {
  if (window.onaProductPolishLoaded) return;
  window.onaProductPolishLoaded = true;

  const products = new WeakMap();
  const carts = new WeakMap();
  const setAttribute = (element, name, value) => {
    if (element.getAttribute(name) !== String(value)) element.setAttribute(name, String(value));
  };
  const inQuickShop = element => Boolean(element.closest('ona-quick-shop, quick-add-modal, [role="dialog"]'));

  function decorateOptions(info) {
    const container = info.querySelector('.product__info-container');
    if (!container) return;
    info.querySelectorAll('.quantity__input').forEach(input => {
      const sourceLabel = input.labels?.[0]?.querySelector('span')?.textContent.trim();
      if (sourceLabel && !input.getAttribute('aria-label')) input.setAttribute('aria-label', sourceLabel);
    });
    let hasSizeHelp = false;
    info.querySelectorAll('variant-selects .product-form__input').forEach(group => {
      const input = group.querySelector('input[data-option-name], select');
      const label = group.querySelector('.form__label');
      if (!input || !label) return;
      const optionName = input.dataset.optionName || input.name.replace(/^options\[|\]$/g, '').replace(/-\d+$/, '');
      const selected = group.querySelector('input:checked') || group.querySelector('select');
      if (!label.querySelector('[data-selected-value]')) {
        let value = label.querySelector('.ona-option-value');
        if (!value) { value = document.createElement('span'); value.className = 'ona-option-value'; label.append(value); }
        const displayValue = selected?.selectedOptions?.[0]?.dataset.optionDisplayValue || selected?.dataset.optionDisplayValue || selected?.value || '';
        if (value.textContent !== displayValue) value.textContent = displayValue;
      }
      if (!/^size$/i.test(optionName)) return;
      hasSizeHelp = true;
      if (group.querySelector('[data-ona-fit-help]')) return;
      if (!inQuickShop(info) && document.querySelector('[data-curtain-pdp-dialog]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ona-size-help-link';
        button.dataset.onaFitHelp = '';
        button.dataset.curtainPdpMeasure = '';
        button.setAttribute('aria-haspopup', 'dialog');
        button.textContent = 'Measuring guide';
        if (label.tagName === 'LEGEND') label.append(button);
        else {
          // A dropdown's label cannot contain another labelable control.
          const row = document.createElement('div');
          row.className = 'ona-option-label-row';
          label.replaceWith(row);
          row.append(label, button);
        }
      } else {
        // Keep quick shop to one dialog; inline help leaves every option intact.
        const details = document.createElement('details');
        details.className = 'ona-size-help-inline';
        details.dataset.onaFitHelp = '';
        const summary = document.createElement('summary');
        summary.textContent = 'Measuring help';
        const text = document.createElement('p');
        text.textContent = 'Compare the listed width and drop with the span and hanging height of your window. Check the product’s panel or pair contents. Your hardware and heading affect the installed drop.';
        details.append(summary, text);
        group.append(details);
      }
    });
    container.classList.toggle('ona-has-size-help', hasSizeHelp);
  }

  function decorateGallery(info) {
    info.querySelectorAll('ona-product-media:not([hidden])').forEach(gallery => {
      const rail = gallery.querySelector('[data-ona-thumbnails]');
      const active = rail?.querySelector('[aria-current="true"]');
      if (!rail || !active) return;
      rail.querySelectorAll('button').forEach(button => { button.tabIndex = button === active ? 0 : -1; });
      const state = products.get(info);
      if (state.activeThumbnail === active) return;
      state.activeThumbnail = active;
      // Pointer browsing owns the rail offset; keyboard selections still follow into view.
      if (rail.hasAttribute('data-ona-pointer-browsing')) return;
      const railRect = rail.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const vertical = getComputedStyle(rail).flexDirection === 'column';
      if (vertical) {
        if (activeRect.top < railRect.top) rail.scrollTop += activeRect.top - railRect.top - 4;
        else if (activeRect.bottom > railRect.bottom) rail.scrollTop += activeRect.bottom - railRect.bottom + 4;
      } else {
        if (activeRect.left < railRect.left) rail.scrollLeft += activeRect.left - railRect.left - 4;
        else if (activeRect.right > railRect.right) rail.scrollLeft += activeRect.right - railRect.right + 4;
      }
    });
  }

  function enhanceProduct(info) {
    if (products.has(info)) return;
    const state = { frame: null, activeThumbnail: null };
    products.set(info, state);
    const update = () => {
      state.frame = null;
      if (!info.isConnected) { state.observer.disconnect(); products.delete(info); return; }
      decorateOptions(info);
      decorateGallery(info);
    };
    const schedule = () => { if (state.frame === null) state.frame = requestAnimationFrame(update); };
    state.observer = new MutationObserver(schedule);
    state.observer.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current', 'hidden'] });
    info.addEventListener('change', schedule);
    info.addEventListener('keydown', event => {
      if (!['Home', 'End'].includes(event.key) || !event.target.closest('[data-ona-thumbnail]')) return;
      const gallery = event.target.closest('ona-product-media');
      if (!gallery || gallery.querySelector('dialog[open]')) return;
      const thumbnails = [...gallery.querySelectorAll('[data-ona-thumbnail]')];
      const target = event.key === 'Home' ? thumbnails[0] : thumbnails[thumbnails.length - 1];
      if (!target) return;
      event.preventDefault();
      target.click();
      target.focus({ preventScroll: true });
    });
    update();
  }

  function enhanceCart(cart) {
    if (carts.has(cart)) return;
    const state = { frame: null };
    carts.set(cart, state);
    const update = () => {
      state.frame = null;
      if (!cart.isConnected) { state.observer.disconnect(); carts.delete(cart); return; }
      const busy = Boolean(cart.querySelector('.cart__items--disabled'));
      setAttribute(cart, 'aria-busy', busy);
      cart.querySelectorAll('.cart-item').forEach(row => {
        const input = row.querySelector('.quantity__input');
        const error = row.querySelector('.cart-item__error');
        if (!input || !error) return;
        const hasError = Boolean(error.querySelector('.cart-item__error-text')?.textContent.trim());
        setAttribute(input, 'aria-invalid', hasError);
        if (hasError && error.id) setAttribute(input, 'aria-errormessage', error.id);
        else input.removeAttribute('aria-errormessage');
      });
      const anyCartBusy = Boolean(document.querySelector('cart-items .cart__items--disabled, cart-drawer-items .cart__items--disabled'));
      document.querySelectorAll('.cart__checkout-button').forEach(button => {
        if (anyCartBusy) {
          if (!button.hasAttribute('data-ona-pending-checkout')) {
            button.dataset.onaPendingCheckout = button.getAttribute('aria-disabled') || '';
          }
          setAttribute(button, 'aria-disabled', true);
        } else if (button.hasAttribute('data-ona-pending-checkout')) {
          const previous = button.dataset.onaPendingCheckout;
          if (previous) setAttribute(button, 'aria-disabled', previous);
          else button.removeAttribute('aria-disabled');
          delete button.dataset.onaPendingCheckout;
        }
      });
    };
    const schedule = () => { if (state.frame === null) state.frame = requestAnimationFrame(update); };
    state.observer = new MutationObserver(schedule);
    state.observer.observe(cart, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    update();
  }

  class OnaRelatedCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('[data-ona-carousel-track]');
      this.items = [...this.querySelectorAll('[data-ona-carousel-item]')];
      this.previous = this.querySelector('[data-ona-carousel-previous]');
      this.next = this.querySelector('[data-ona-carousel-next]');
      this.controls = this.querySelector('[data-ona-carousel-controls]');
      this.status = this.querySelector('[data-ona-carousel-status]');
      if (!this.track || !this.items.length) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.previous.addEventListener('click', () => this.advance(-1), { signal });
      this.next.addEventListener('click', () => this.advance(1), { signal });
      this.track.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.advance((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.reveal(event.key === 'Home' ? 0 : this.items.length - 1);
        }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this.track);
      this.schedule();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }
    visibleItems() {
      const bounds = this.track.getBoundingClientRect();
      return this.items.reduce((visible, item, index) => {
        const rect = item.getBoundingClientRect();
        if (Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left) > Math.min(20, rect.width / 2)) visible.push(index);
        return visible;
      }, []);
    }
    update() {
      const visible = this.visibleItems();
      if (!visible.length) return;
      this.visible = visible;
      this.controls.hidden = this.track.scrollWidth <= this.track.clientWidth + 2;
      const bounds = this.track.getBoundingClientRect();
      const first = this.items[0].getBoundingClientRect();
      const last = this.items[this.items.length - 1].getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.previous.disabled = rtl ? first.right <= bounds.right + 5 : first.left >= bounds.left - 5;
      this.next.disabled = rtl ? last.left >= bounds.left - 5 : last.right <= bounds.right + 5;
      const text = `Showing ${visible[0] + 1}–${visible[visible.length - 1] + 1} of ${this.items.length} curtain styles`;
      if (this.status.textContent !== text) this.status.textContent = text;
    }
    advance(direction) {
      const visible = this.visibleItems();
      if (!visible.length) return;
      const target = direction > 0 ? visible[visible.length - 1] + 1 : Math.max(0, visible[0] - visible.length);
      this.reveal(Math.min(this.items.length - 1, target));
    }
    reveal(index) {
      const item = this.items[index];
      if (!item) return;
      const bounds = this.track.getBoundingClientRect();
      const target = item.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const distance = rtl ? target.right - bounds.right : target.left - bounds.left;
      this.track.scrollBy({ left: distance, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.controller = null;
    }
  }
  if (!customElements.get('ona-related-carousel')) customElements.define('ona-related-carousel', OnaRelatedCarousel);

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root.matches?.('product-info.curtain-pdp')) enhanceProduct(root);
    if (root.matches?.('cart-items, cart-drawer-items')) enhanceCart(root);
    root.querySelectorAll('product-info.curtain-pdp').forEach(enhanceProduct);
    root.querySelectorAll('cart-items, cart-drawer-items').forEach(enhanceCart);
  }
  // Dawn swaps option groups, bag sections and quick-shop content after native responses.
  const documentObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => mutation.addedNodes.forEach(node => { if (node.nodeType === Node.ELEMENT_NODE) scan(node); }));
  });
  function start() {
    scan(document);
    documentObserver.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => scan(event.target));

  document.addEventListener('click', event => {
    const target = event.target.closest('.quantity__button, cart-remove-button, .cart__checkout-button');
    if (!target) return;
    const pending = target.closest('cart-items, cart-drawer-items')?.querySelector('.cart__items--disabled');
    const pendingCheckout = target.matches('.cart__checkout-button') && document.querySelector('cart-items .cart__items--disabled, cart-drawer-items .cart__items--disabled');
    if (pending || pendingCheckout) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('change', event => {
    const input = event.target.closest('cart-items .quantity__input, cart-drawer-items .quantity__input');
    if (input?.closest('cart-items, cart-drawer-items')?.querySelector('.cart__items--disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
})();
